import { ReactElement, ReactNode, useEffect } from 'react'
import create from 'zustand'
import { combine } from 'zustand/middleware'
import { resolveSelectedElementType } from '@editor/utils'
import { useModelStore } from '../modelStore'
import { useSelectionStore } from '../selectionStore'
import { isMultiSelectionInMode } from '../selectionStore/reconcile'

interface Props {
  children: ReactNode
}

interface EditElementStoreState {
  activeElement: string | null
  multiActiveElements: string[]
  activeOpening: string | null
  multiActiveOpenings: string[]
  isDrawingLintel: boolean

  // used for roof extending
  activeRoofPointIndex: number | null

  drawSlabOnElement: boolean
  drawRoofOnElement: boolean
}

interface EditElementStoreType extends EditElementStoreState {
  setActiveElement: (guid: string | null) => void
  setMultiActiveElements: (guids: string[]) => void
  setActiveOpening: (openingGuid: string | null) => void
  setMultiActiveOpenings: (openingGuids: string[]) => void
  clear: () => void
  setIsDrawingLintel: (isDrawing: boolean) => void
  setActiveRoofPointIndex: (index: number | null) => void
  setDrawSlabOnElement: (drawSlabOnElement: boolean) => void
  setDrawRoofOnElement: (drawRoofOnElement: boolean) => void
}

const drawInitialState = {
  isDrawingLintel: false,
  activeRoofPointIndex: null as number | null,
  drawSlabOnElement: false,
  drawRoofOnElement: true,
}

/** Drawing state that is genuinely edit state (not selection) and stays in this store. */
const useEditElementDrawStore = create(
  combine({ ...drawInitialState }, set => ({
    setIsDrawingLintel: (isDrawingLintel: boolean) => set({ isDrawingLintel }),
    setActiveRoofPointIndex: (activeRoofPointIndex: number | null) => set({ activeRoofPointIndex }),
    setDrawSlabOnElement: (drawSlabOnElement: boolean) => set({ drawSlabOnElement }),
    setDrawRoofOnElement: (drawRoofOnElement: boolean) => set({ drawRoofOnElement }),
    clearDrawState: () => set({ ...drawInitialState }),
  })),
)

const emptyGuids: string[] = []

/**
 * These write through with `adopt: 'keep'`: the compat callers (floorplan clicks, IFC panels,
 * drawing tools) never expected setting an element to switch the editor mode. The reconciler still
 * guarantees the resulting mode can hold the selection, falling back to neutral otherwise.
 */
const actions = {
  setActiveElement: (guid: string | null) => {
    const { selection, mode, select } = useSelectionStore.getState()
    if (guid)
      select(
        {
          type: 'planar',
          elementType: resolveSelectedElementType(useModelStore.getState().model, guid),
          guids: [guid],
        },
        { adopt: 'keep' },
      )
    else if (selection?.type === 'planar' && !isMultiSelectionInMode(selection, mode)) select(null)
  },

  setMultiActiveElements: (guids: string[]) => {
    const { selection, mode, select } = useSelectionStore.getState()
    if (guids.length) select({ type: 'planar', guids, isMulti: true }, { adopt: 'keep' })
    else if (selection?.type === 'planar' && isMultiSelectionInMode(selection, mode)) select(null)
  },

  setActiveOpening: (openingGuid: string | null) => {
    const { selection, subSelection, selectSub } = useSelectionStore.getState()
    if (openingGuid) {
      const wallGuid =
        selection?.type === 'planar' && selection.guids.length === 1 ? selection.guids[0] : null
      selectSub({ type: 'opening', guids: [openingGuid], wallGuid })
    } else if (subSelection?.type === 'opening' && !subSelection.isMulti) selectSub(null)
  },

  setMultiActiveOpenings: (openingGuids: string[]) => {
    const { subSelection, selectSub } = useSelectionStore.getState()
    if (openingGuids.length)
      selectSub({ type: 'opening', guids: openingGuids, isMulti: true, wallGuid: null })
    else if (subSelection?.type === 'opening' && subSelection.isMulti) selectSub(null)
  },

  clear: () => {
    const { selection, subSelection, select, selectSub } = useSelectionStore.getState()
    if (selection?.type === 'planar') select(null)
    else if (subSelection?.type === 'opening') selectSub(null)
    useEditElementDrawStore.getState().clearDrawState()
  },

  setIsDrawingLintel: (isDrawing: boolean) =>
    useEditElementDrawStore.getState().setIsDrawingLintel(isDrawing),
  setActiveRoofPointIndex: (index: number | null) =>
    useEditElementDrawStore.getState().setActiveRoofPointIndex(index),
  setDrawSlabOnElement: (drawSlabOnElement: boolean) =>
    useEditElementDrawStore.getState().setDrawSlabOnElement(drawSlabOnElement),
  setDrawRoofOnElement: (drawRoofOnElement: boolean) =>
    useEditElementDrawStore.getState().setDrawRoofOnElement(drawRoofOnElement),
}

const deriveCompatState = (
  selection: SceneElementSelection | null,
  subSelection: SceneElementSubSelection | null,
  mode: EditorMode,
  drawState: typeof drawInitialState,
): EditElementStoreType => {
  const planar = selection?.type === 'planar' ? selection : null
  const isMulti = isMultiSelectionInMode(planar, mode)
  const openingSub = subSelection?.type === 'opening' ? subSelection : null
  // An opening clicked in the scene is a *primary* selection (openings mode), not a
  // sub-selection — compat consumers (e.g. the OpeningsMesh highlight) still expect it as the
  // active opening either way.
  const primaryOpening =
    planar && !isMulti && planar.elementType === 'opening' && planar.guids.length === 1
      ? planar.guids[0]
      : null

  return {
    activeElement: planar && !isMulti && planar.guids.length === 1 ? planar.guids[0] : null,
    multiActiveElements: planar && isMulti ? planar.guids : emptyGuids,
    activeOpening:
      openingSub && !openingSub.isMulti && openingSub.guids.length === 1
        ? openingSub.guids[0]
        : primaryOpening,
    multiActiveOpenings: openingSub?.isMulti ? openingSub.guids : emptyGuids,
    isDrawingLintel: drawState.isDrawingLintel,
    activeRoofPointIndex: drawState.activeRoofPointIndex,
    drawSlabOnElement: drawState.drawSlabOnElement,
    drawRoofOnElement: drawState.drawRoofOnElement,
    ...actions,
  }
}

const computeCompatState = (): EditElementStoreType => {
  const { selection, subSelection, mode } = useSelectionStore.getState()
  return deriveCompatState(selection, subSelection, mode, useEditElementDrawStore.getState())
}

/**
 * Compat layer over the global selection store: the active/multi element and opening selections
 * now live there ('planar' selection + 'opening' sub-selection), so they survive page and
 * floorplan/3D switches; only the drawing flags remain store-local. The zustand-style selector
 * API and `getState()` are preserved for the ~50 existing consumers.
 *
 * The derived state is mirrored into a real store (rather than recomputed from a selector that
 * subscribes to the whole source stores) so consumers get real per-field equality checks — a
 * consumer selecting only `activeElement` no longer re-renders when an unrelated draw flag like
 * `isDrawingLintel` changes.
 */
const useEditElementCompatStore = create<EditElementStoreType>(() => computeCompatState())

// The selection store notifies on every write; recompute the mirror only when an input this
// store actually derives from changed (e.g. not for `activeTab`/`drawAreaLoadElement` writes),
// so the ~50 consumers' selectors don't rerun on unrelated selection-store traffic.
let lastSyncInputs: {
  selection: SceneElementSelection | null
  subSelection: SceneElementSubSelection | null
  mode: EditorMode
  drawState: typeof drawInitialState
} | null = null

const syncCompatState = () => {
  const { selection, subSelection, mode } = useSelectionStore.getState()
  const drawState = useEditElementDrawStore.getState()
  if (
    lastSyncInputs &&
    lastSyncInputs.selection === selection &&
    lastSyncInputs.subSelection === subSelection &&
    lastSyncInputs.mode === mode &&
    lastSyncInputs.drawState === drawState
  )
    return

  lastSyncInputs = { selection, subSelection, mode, drawState }
  useEditElementCompatStore.setState(deriveCompatState(selection, subSelection, mode, drawState))
}
useSelectionStore.subscribe(syncCompatState)
useEditElementDrawStore.subscribe(syncCompatState)

const useEditElementStore = <T,>(selector: (state: EditElementStoreType) => T): T =>
  useEditElementCompatStore(selector)

useEditElementStore.getState = useEditElementCompatStore.getState

const EditElementStoreProvider = ({ children }: Props): ReactElement => {
  // Only the store-local draw state is reset here. The selection itself is owned by the
  // selection store, whose provider clears it on editor unmount — clearing it from this cleanup
  // would override the central keep/reset policy (see selectionStore/README.md).
  useEffect(() => () => useEditElementDrawStore.getState().clearDrawState(), [])

  return <>{children}</>
}

export { EditElementStoreProvider, useEditElementStore }
